//flow
import React, { useState, useEffect } from "react";
import { View, Checkbox, Text, Button, Modal } from "@components";
import { type GSGridColumn } from "@components/gs-grid";

import "./style.scss";

export interface Props {
  allColumns: GSGridColumn[];
  currentReportColumns: string[] | null;
  onSubmit: (gridColumns: GSGridColumn[], currentReportColumns: string[]) => void;
  onChange: (gridColumns: GSGridColumn[], currentReportColumns: string[]) => void;
}

export function ColumnsOrganiser(props: Props) {
  const allColumns = props.allColumns;
  const propsCurrentReportColumns = props.currentReportColumns;

  /**
   *  This array of strings is supposed to be send from the BE but if its not we can produce default one from all columns
   * */
  // Using state since we're about to re-organise the reducers and its better to not play with them for the moment
  const [currentReportColumns, setCurrentReportColumns]: [string[], Function] = useState(
    props.currentReportColumns || getDefaultReportColumns(),
  );

  // This should be provided by the BE
  const [draggedColumn, setDraggedColumn] = useState(null);
  const [draggedOver, setDraggedOver] = useState(null);

  function getDefaultReportColumns() {
    return allColumns
      .filter(c => c.frozen !== "right")
      .filter(c => !c.hasOwnProperty("hidden"))
      .map(c => c.key);
  }
  function getOrderedListOfAllColumns() {
    // Getting the checked (selected) columns
    const res = [
      ...currentReportColumns
        .map(colKey => allColumns.find(c => c.key === colKey))
        .filter(c => c && c.frozen !== "right"),
      // Getting the unselected ones
      ...allColumns.filter(c => !currentReportColumns.includes(c.key) || c.frozen === "right"),
    ];
    return res;
  }
  function handleCancel() {
    props.onCancel && props.onCancel();
  }
  function handleChange() {
    props.onSubmit && props.onSubmit(currentReportColumns);
    handleCancel();
  }
  function handleSubmit() {
    props.onSubmit && props.onSubmit(currentReportColumns);
    handleCancel();
  }
  function setCols(cols: string[]) {
    setCurrentReportColumns(cols);
  }
  function toggleVisibleColumn(column) {
    return function() {
      let cols;
      if (currentReportColumns.includes(column)) {
        const removed = currentReportColumns.filter(col => col !== column);
        cols = removed;
      } else {
        cols = [...currentReportColumns, column];
      }
      setCols(cols);
    };
  }
  function moveColumn(draggedCol, columnTarget) {
    const cols = [...currentReportColumns];
    const columnIdx = cols.indexOf(draggedCol);
    const beforeColumn = cols.indexOf(columnTarget);
    cols.splice(beforeColumn, 0, cols.splice(columnIdx, 1)[0]);
    setCols(cols);
  }
  function onDragStart(column) {
    return function(e) {
      setDraggedColumn(column);
    };
  }
  function onDragOver(column) {
    return function(e) {
      const overCol = allColumns.find(c => c.key == column);
      if (overCol.frozen) return;
      e.preventDefault();

      setDraggedOver(column);
      moveColumn(draggedColumn, column);
    };
  }
  function onDragEnd(column) {
    return function(e) {
      moveColumn(draggedColumn, draggedOver);
      setDraggedColumn(null);
    };
  }

  return (
    <Modal rightPanel width="400px" onToggle={props.onBackdropClick}>
      <View flex={1} padding={[0, 6, 6, 6]} gap={3}>
        {/* Modal header */}
        <View
          direction="row"
          separator="bottom"
          padding={[3, 0, 3, 0]}
          backgroundColor={"white"}
          position="sticky"
          zIndex={5}
          top={0}
          justifyContent="space-between"
        >
          <View flex={1}>
            <Text type="h4" text="Columns" />
          </View>
          <View gap={3} direction="row">
            <Button onClick={props.onBackdropClick} color="secondary" label="Cancel" />

            <Button
              onClick={handleSubmit}
              color="primary"
              materialIcon="replay_circle_filled"
              label="Submit"
            />
          </View>
        </View>
        <View direction="row">
          {/** TODO: Debugging - remove when done START / currentReportColumns && (
            <View
              direction="row"
              justifyContent="space-between"
              backgroundColor="white"
              separator="right"
              gap={5}
            >
              <View>
                <Text type="h3" text="currentGridColumns" />
                <View>
                  {currentGridColumns &&
                    currentGridColumns.map((column: GSGridColumn) => {
                      return (
                        <View key={column.key} direction="row">
                          <View gap={2} flex={1} direction="row">
                            <Text text={column.key} /> <Text />
                          </View>
                        </View>
                      );
                    })}
                </View>
              </View>

              <View>
                <Text type="h3" text="currentReportColumns" />
                <View>
                  {currentReportColumns.map((column: string) => {
                    return (
                      <View key={column} direction="row">
                        <View gap={2} flex={1} direction="row">
                          <Text text={column} /> <Text />
                        </View>
                      </View>
                    );
                  })}
                </View>
              </View>
            </View>
          ) /** TODO: Debugging - remove when done END */}
          <View flex={1} className={`columns-organiser ${(draggedColumn && "sorting") || ""}`}>
            {getOrderedListOfAllColumns().map((column: GSGridColumn) => {
              const isFrozen = column.hasOwnProperty("frozen");
              const isVisible = currentReportColumns.includes(column.key);
              return (
                <View
                  key={column.key}
                  draggable={!isFrozen && isVisible}
                  className={`column ${(draggedColumn &&
                    column.key === draggedColumn &&
                    "dragged") ||
                    ""}`}
                  onDragStart={isVisible ? onDragStart(column.key) : null}
                  onDragOver={isVisible ? onDragOver(column.key) : null}
                  onDragEnd={isVisible ? onDragEnd(column.key) : null}
                  direction="row"
                  gap={2}
                  padding={2}
                  alignItems="center"
                >
                  <View
                    onClick={!isFrozen ? toggleVisibleColumn(column.key) : () => {}}
                    cursor={!isFrozen && "pointer"}
                    gap={2}
                    flex={1}
                    direction="row"
                  >
                    <Checkbox disabled={isFrozen} checked={isVisible || isFrozen} />
                    <Text text={column.name || "System column"} />
                  </View>
                  {!isFrozen && isVisible && (
                    <View cursor="ns-resize">
                      <Text className="material-icons" text="drag_handle" />
                    </View>
                  )}
                </View>
              );
            })}
          </View>
        </View>
      </View>
    </Modal>
  );
}
